'use client';

import React, { useMemo } from 'react';
import type { Section as SectionType, StepConfig, FormInstance } from '../../types';
import { CardSection } from './CardSection';
import { LegacySection } from './LegacySection';
import { getSectionStatus } from '../../utils/sectionStatus';
import { mergeFormValues } from '../../utils/valueMerger';
import useDictionary from 'lib/hooks/useDictionary';

interface SectionProps {
  section: SectionType;
  sectionIndex: number;
  stepsById: Record<string, StepConfig>;
  completedForms: Record<string, FormInstance[]>;
  firstIncompleteIndex: string | number;
  values: Record<string, unknown>;
  currentSectionId: string | null;
  onSectionClick: (cardOrSectionId: string) => void;
  onSectionEdit: (cardOrSectionId: string, instanceId: string) => void;
  localStorageKey?: string;
  currentStepId?: string;
  currentStep?: StepConfig;
}

export const SectionComponent: React.FC<SectionProps> = ({
  section,
  sectionIndex,
  stepsById,
  completedForms,
  firstIncompleteIndex,
  values,
  currentSectionId,
  onSectionClick,
  onSectionEdit,
  localStorageKey,
  currentStepId,
  currentStep,
}) => {
  // Get merged values for status calculation
  const mergedValues = useMemo(() => {
    return mergeFormValues(values, completedForms, localStorageKey);
  }, [values, localStorageKey, completedForms]);

  const { getDictionaryValue } = useDictionary();

  // Calculate section status using merged values - pass localStorageKey
  const sectionStatus = getSectionStatus(
    section,
    completedForms,
    currentSectionId,
    mergedValues,
    localStorageKey,
    getDictionaryValue
  );
  // Check if section has cards
  if (section.cards && section.cards.length > 0) {
    return (
      <CardSection
        section={section}
        sectionIndex={sectionIndex}
        stepsById={stepsById}
        completedForms={completedForms}
        firstIncompleteIndex={firstIncompleteIndex}
        values={values}
        sectionStatus={sectionStatus}
        onSectionClick={onSectionClick}
        onSectionEdit={onSectionEdit}
        localStorageKey={localStorageKey}
        currentStepId={currentStepId}
        currentStep={currentStep}
      />
    );
  }

  // Legacy: Handle sections without cards (backward compatibility)
  const groupKey = section.groupKey || section.id;
  const completedInstances = completedForms[groupKey] || [];
  const isFirstIncomplete =
    firstIncompleteIndex === sectionIndex && completedInstances.length === 0;
  const isCTAEnabled = isFirstIncomplete || completedInstances.length > 0;

  // Get all steps for this section
  const sectionSteps = section.stepIds
    ? section.stepIds.map((stepId) => stepsById[stepId]).filter((step) => step !== undefined)
    : [];

  return (
    <LegacySection
      section={section}
      sectionIndex={sectionIndex}
      steps={sectionSteps as StepConfig[]}
      completedInstances={completedInstances}
      isCTAEnabled={isCTAEnabled}
      sectionStatus={sectionStatus}
      onSectionClick={onSectionClick}
      onSectionEdit={onSectionEdit}
    />
  );
};
